import { useRef } from "react";
import { m, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { imgUrl, waLink } from "../../lib/api";
import { scrollToId, WhatsAppIcon } from "./shared";

export default function Hero() {
  const { content } = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  if (!content) return <section className="min-h-screen bg-olivedark" />;

  const { hero, contacts } = content;
  const h1 = hero.h1 || "Celebrante de Casamentos em Belo Horizonte e Minas Gerais";
  const words = h1.split(" ");
  const lines = [];
  for (let i = 0; i < words.length; i += 3) lines.push(words.slice(i, i + 3).join(" "));

  return (
    <section id="inicio" ref={ref} className="relative min-h-screen overflow-hidden bg-olivedark grain" data-testid="hero-section">
      <m.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={imgUrl(hero.image)}
          alt="Dom Frei Lucas Macieira, celebrante de casamentos, conduzindo cerimônia de casamento"
          className="w-full h-full object-cover"
          fetchPriority="high"
          data-testid="hero-image"
        />
      </m.div>
      <div className="absolute inset-0 bg-gradient-to-t from-olivedark via-olivedark/50 to-olivedark/25" />
      <div className="absolute inset-0 bg-gradient-to-r from-olivedark/60 via-transparent to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-screen flex flex-col justify-end pb-24 pt-40">
        <div className="max-w-4xl">
          <m.p
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.8 }}
            className="font-script text-2xl sm:text-3xl lg:text-4xl text-goldlight"
            data-testid="hero-kicker"
          >
            {hero.kicker || "Dom Frei Lucas Macieira"}
          </m.p>

          <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl text-cream leading-[1.15] tracking-tight" data-testid="hero-headline">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1 -mb-1">
                <m.span
                  className="block"
                  initial={false}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.25 + i * 0.12, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </m.span>
              </span>
            ))}
          </h1>

          <m.p
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.7 }}
            className="mt-5 font-serif italic text-xl sm:text-2xl text-goldlight/95 leading-snug"
            data-testid="hero-poetic-line"
          >
            {hero.title}
          </m.p>

          <m.p
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-4 text-base sm:text-lg font-light text-cream/85 max-w-2xl leading-relaxed"
            data-testid="hero-subtitle"
          >
            {hero.subtitle}
          </m.p>

          <m.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-consult-button"
              onClick={() => scrollToId("consulte")}
              className="inline-flex items-center gap-2 bg-gold hover:bg-golddark text-olivedark font-semibold text-sm tracking-wide px-7 py-4 rounded-full transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] shadow-lg shadow-gold/25"
            >
              CONSULTAR MINHA DATA
            </button>
            <a
              data-testid="hero-whatsapp-button"
              href={waLink(contacts.whatsapp_number, contacts.whatsapp_message)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => window.gtag && window.gtag("event", "click_whatsapp", { location: "hero" })}
              className="inline-flex items-center gap-2 bg-whatsapp hover:bg-whatsappdark text-white font-semibold text-sm tracking-wide px-7 py-4 rounded-full transition-all duration-300 hover:scale-[1.03] active:scale-[0.98]"
            >
              <WhatsAppIcon className="w-4 h-4" />
              FALAR COM PADRE LUCAS NO WHATSAPP
            </a>
          </m.div>

          <m.div
            initial={false}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 1 }}
            className="mt-12"
          >
            <p className="font-script text-2xl sm:text-3xl text-goldlight" data-testid="hero-signature">
              “{hero.signature}”
            </p>
            <p className="text-xs uppercase tracking-[0.3em] text-cream/60 mt-2">Padre Lucas Macieira · Celebrante de Casamentos</p>
          </m.div>
        </div>
      </div>

      <m.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("diferenciais")}
        initial={false}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 right-6 sm:right-10 z-10 text-cream/70 hover:text-goldlight transition-colors"
        aria-label="Rolar para baixo"
      >
        <m.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ChevronDown className="w-8 h-8" />
        </m.div>
      </m.button>
    </section>
  );
}
